"use client";
import Button from "@/designs/Button";
import Input from "@/designs/Input";
import { useSignupMutation } from "@/graphql/generated/schema";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import React, { useState } from "react";
import toast from "react-hot-toast";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { twMerge } from "tailwind-merge";

const Signup = () => {
  const [signup, { loading }] = useSignupMutation();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNo: "",
    password: "",
    confirmPassword: "",
    address: {
      street: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
    },
    companyName: "",
  });
  const router = useRouter();
  const handleChange = (name: string, value: string) => {
    if (name.startsWith("address.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const {
      firstName,
      lastName,
      email,
      phoneNo,
      password,
      confirmPassword,
      address,
      companyName,
    } = formData;

    // Basic validation
    if (!firstName || !email || !password || !confirmPassword) {
      toast.error("All fields are required.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      const { data } = await signup({
        variables: {
          input: {
            firstName,
            lastName,
            email,
            phoneNo,
            password,
            address,
            companyName,
          },
        },
      });

      if (data?.signup?.error) {
        toast.error(data.signup.error.message);
      } else {
        toast.success("Account created successfully!");
        router.push("/login");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phoneNo: "",
          password: "",
          confirmPassword: "",
          address: {
            street: "",
            city: "",
            state: "",
            country: "",
            pincode: "",
          },
          companyName: "",
        });
      }
    } catch (err: any) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <div    className={twMerge(
            "flex flex-col md:flex-row md:h-screen overflow-y-auto bg-gray-50",
          )}>
      {/* Left Side: Logo */}
      <div
        className={twMerge(
          "flex justify-center items-center w-full md:h-full transition-colors",
          "bg-white",
        )}
      >
        <Image
          src="/Logo.jpg"
          width={700}
          height={250}
          alt="logo"
          className="object-contain"
          priority
        />
      </div>

      {/* Right Side: Signup Form */}
      <div className="flex justify-center items-center bg-[#004175] w-full md:w-1/2 h-full p-6 overflow-y-auto">
        <div
          className={twMerge(
            "space-y-4 w-full max-w-lg rounded-2xl shadow-xl p-4 transition-colors border",
            "bg-white border-transparent",
          )}
        >
          <div className="flex flex-col items-center">
            <h1 className={twMerge(" font-bold mt-4 transition-colors ")}>
              Create Account
            </h1>
            <p className={twMerge("transition-colors text-gray-600")}>
              Join us today!
            </p>
          </div>

          <form className="space-y-2" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Input
                type="text"
                name="firstName"
                placeholder="First Name"
                // label="First Name"
                value={formData.firstName}
                onChange={(value) => handleChange("firstName", value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md lg:min-w-[150px]"
              />
              <Input
                type="text"
                name="lastName"
                placeholder="Last Name"
                // label="Last Name"
                value={formData.lastName}
                onChange={(value) => handleChange("lastName", value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md lg:min-w-[150px]"
              />
            </div>

            <Input
              type="email"
              name="email"
              // label="Email"
              required
              placeholder="you@example.com"
              value={formData.email}
              onChange={(value) => handleChange("email", value)}
            />

            <PhoneInput
              country={"us"} // default country
              value={formData.phoneNo}
              onChange={(value, countryData: any) => {
                const fullNumber = `+${countryData.dialCode}${value.replace(
                  countryData.dialCode,
                  "",
                )}`;
                setFormData({
                  ...formData,
                  phoneNo: fullNumber,
                });
              }}
              inputProps={{
                name: "phone",
                required: true,
                autoFocus: false,
              }}
              containerClass="w-full"
              inputClass="!w-full !py-2 !pl-12 !border-gray-300 !rounded-md"
            />

            <Input
              type="text"
              name="companyName"
              // label="Company Name"
              placeholder="Company Name"
              required
              value={formData.companyName}
              onChange={(value) => handleChange("companyName", value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="text"
                name="address.street"
                // label="Street"
                placeholder="Street"
                value={formData.address.street}
                onChange={(value) => handleChange("address.street", value)}
                className="lg:min-w-[150px]"
              />

              <Input
                type="text"
                name="address.city"
                // label="City"
                placeholder="City"
                value={formData.address.city}
                onChange={(value) => handleChange("address.city", value)}
                className="lg:min-w-[150px]"
              />

              <Input
                type="text"
                name="address.state"
                // label="State"
                placeholder="State"
                value={formData.address.state}
                onChange={(value) => handleChange("address.state", value)}
                className="lg:min-w-[150px]"
              />

              <Input
                type="text"
                name="address.country"
                // label="Country"
                placeholder="Country"
                value={formData.address.country}
                onChange={(value) => handleChange("address.country", value)}
                className="lg:min-w-[150px]"
              />

              <Input
                type="text"
                name="address.pincode"
                // label="Pincode"
                placeholder="Pincode"
                value={formData.address.pincode}
                onChange={(value) => handleChange("address.pincode", value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="password"
                name="password"
                placeholder="Password"
                // label="Password"
                required
                value={formData.password}
                onChange={(value) => handleChange("password", value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md lg:min-w-[150px]"
              />
              <Input
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                // label="Confirm Password"
                required
                value={formData.confirmPassword}
                onChange={(value) => handleChange("confirmPassword", value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md lg:min-w-[150px]"
              />
            </div>

            <Button type="submit" loading={loading}>
              Sign Up
            </Button>

            <div className="text-center text-sm pt-2">
              Already have an account?{" "}
              <Link
                href="/login"
                className="text-[#004175] font-semibold hover:underline"
              >
                Login here
              </Link>
            </div>

            <div className="flex justify-center gap-4 text-xs pt-4 border-t mt-4 text-gray-400">
              <Link
                href="/privacy-policy"
                className="hover:text-[#004175] hover:underline transition-colors uppercase font-medium"
              >
                Privacy Policy
              </Link>
              <span>•</span>
              <Link
                href="/support"
                className="hover:text-[#004175] hover:underline transition-colors uppercase font-medium"
              >
                Support
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
